import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
	Button,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import {
	createMonitoringWritingAssistantPersona,
	deleteMonitoringWritingAssistantPersona,
	listMonitoringWritingAssistantPersonas,
	updateMonitoringWritingAssistantPersona,
} from "../../../services/monitoringApi";

const EMPTY_FORM = {
	name: "",
	profession: "",
	gender: "",
	ageRange: "",
	location: "",
	language: "English",
	likes: "",
	dislikes: "",
	interests: "",
	tone: "",
	notes: "",
};

function personaField(persona, camel, snake) {
	return persona?.[camel] ?? persona?.[snake] ?? "";
}

function personaToForm(persona) {
	const name = personaField(persona, "name", "name");
	return {
		name,
		profession: personaField(persona, "profession", "profession") || name,
		gender: personaField(persona, "gender", "gender"),
		ageRange: personaField(persona, "ageRange", "age_range"),
		location: personaField(persona, "location", "location"),
		language: personaField(persona, "language", "language") || "English",
		likes: personaField(persona, "likes", "likes"),
		dislikes: personaField(persona, "dislikes", "dislikes"),
		interests: personaField(persona, "interests", "interests"),
		tone: personaField(persona, "tone", "tone"),
		notes: personaField(persona, "notes", "notes"),
	};
}

function personaInitial(name) {
	const trimmed = String(name ?? "").trim();
	return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

function personaDetails(persona) {
	return [
		{
			key: "profession",
			label: "Profession",
			value: personaField(persona, "profession", "profession"),
		},
		{ key: "tone", label: "Tone", value: personaField(persona, "tone", "tone") },
		{ key: "location", label: "Location", value: personaField(persona, "location", "location") },
		{ key: "age", label: "Age", value: personaField(persona, "ageRange", "age_range") },
		{ key: "gender", label: "Sex", value: personaField(persona, "gender", "gender") },
		{ key: "language", label: "Language", value: personaField(persona, "language", "language") },
	].filter((item) => item.value);
}

function PersonaCard({ persona, onEdit, onDelete }) {
	const name = personaField(persona, "name", "name");
	const notes = personaField(persona, "notes", "notes");
	const details = personaDetails(persona);
	const tone = personaField(persona, "tone", "tone");

	return (
		<article className="monitoring__writing-persona-card">
			<div className="monitoring__writing-persona-card-main">
				<span className="monitoring__writing-persona-avatar" aria-hidden>
					{personaInitial(name)}
				</span>
				<div className="monitoring__writing-persona-card-copy">
					<div className="monitoring__writing-persona-card-title-row">
						<h4 className="monitoring__writing-persona-name">{name}</h4>
						{tone ? (
							<span className="monitoring__writing-persona-tone-badge">{tone}</span>
						) : null}
					</div>
					{details.length > 0 ? (
						<ul className="monitoring__writing-persona-meta-list">
							{details
								.filter((item) => item.key !== "tone")
								.map((item) => (
									<li key={item.key}>
										<span className="monitoring__writing-persona-meta-label">
											{item.label}
										</span>
										<span>{item.value}</span>
									</li>
								))}
						</ul>
					) : (
						<p className="monitoring__writing-persona-meta-empty mb-0">
							No additional details yet.
						</p>
					)}
					{notes ? (
						<p className="monitoring__writing-persona-notes mb-0">{notes}</p>
					) : null}
				</div>
			</div>
			<div className="monitoring__writing-persona-card-actions">
				<button
					type="button"
					className="monitoring__writing-setup-icon-btn"
					onClick={() => onEdit(persona)}
					aria-label={`Edit ${name}`}
				>
					<Pencil size={14} aria-hidden />
				</button>
				<button
					type="button"
					className="monitoring__writing-setup-icon-btn monitoring__writing-setup-icon-btn--danger"
					onClick={() => onDelete(persona)}
					aria-label={`Delete ${name}`}
				>
					<Trash2 size={14} aria-hidden />
				</button>
			</div>
		</article>
	);
}

function PersonaDeleteModal({ persona, deleting, error, onClose, onConfirm }) {
	const name = persona ? personaField(persona, "name", "name") : "";

	return (
		<Modal
			isOpen={persona != null}
			toggle={deleting ? undefined : onClose}
			centered
			size="md"
			modalClassName="monitoring__writing-persona-modal-overlay"
			className="monitoring__writing-persona-delete-modal"
			backdrop={deleting ? "static" : true}
			keyboard={!deleting}
		>
			<ModalHeader toggle={deleting ? undefined : onClose}>Delete persona</ModalHeader>
			<ModalBody className="monitoring__writing-persona-delete-modal-body">
				<p className="mb-0">
					Delete <strong>{name || "this persona"}</strong>? This cannot be undone.
				</p>
				{error ? <p className="text-danger small mb-0 mt-3">{error}</p> : null}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={onClose} disabled={deleting}>
					Cancel
				</Button>
				<Button color="danger" onClick={onConfirm} disabled={deleting}>
					{deleting ? <Spinner size="sm" className="me-2" /> : null}
					Delete persona
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function PersonaModal({ isOpen, mode, initialForm, saving, error, onClose, onSave }) {
	const [form, setForm] = useState(EMPTY_FORM);

	useEffect(() => {
		if (isOpen) {
			setForm(initialForm || EMPTY_FORM);
		}
	}, [isOpen, initialForm]);

	function setField(key, value) {
		setForm((previous) => ({ ...previous, [key]: value }));
	}

	function handleSubmit(event) {
		event.preventDefault();
		onSave(form);
	}

	return (
		<Modal
			isOpen={isOpen}
			toggle={saving ? undefined : onClose}
			centered
			modalClassName="monitoring__writing-persona-modal-overlay"
			className="monitoring__writing-persona-modal"
			backdrop={saving ? "static" : true}
			keyboard={!saving}
		>
			<form onSubmit={handleSubmit}>
				<ModalHeader toggle={saving ? undefined : onClose}>
					{mode === "edit" ? "Edit persona" : "New persona"}
				</ModalHeader>
				<ModalBody className="monitoring__writing-persona-modal-body">
					<div className="monitoring__writing-persona-form">
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-name">
								Persona name <span className="text-danger">*</span>
							</Label>
							<Input
								id="writing-persona-name"
								value={form.name}
								onChange={(event) => setField("name", event.target.value)}
								disabled={saving}
								required
							/>
							<p className="text-muted small mb-0">
								Label for lists only — not sent to AI engines.
							</p>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-profession">
								Profession <span className="text-danger">*</span>
							</Label>
							<Input
								id="writing-persona-profession"
								value={form.profession}
								onChange={(event) => setField("profession", event.target.value)}
								disabled={saving}
								required
							/>
						</div>
						<div className="monitoring__writing-persona-field">
							<Label for="writing-persona-gender">Sex</Label>
							<Input
								id="writing-persona-gender"
								value={form.gender}
								onChange={(event) => setField("gender", event.target.value)}
								disabled={saving}
								placeholder="Optional"
							/>
						</div>
						<div className="monitoring__writing-persona-field">
							<Label for="writing-persona-age">Age range</Label>
							<Input
								id="writing-persona-age"
								value={form.ageRange}
								onChange={(event) => setField("ageRange", event.target.value)}
								disabled={saving}
								placeholder="e.g. 30–45"
							/>
						</div>
						<div className="monitoring__writing-persona-field">
							<Label for="writing-persona-location">Location</Label>
							<Input
								id="writing-persona-location"
								value={form.location}
								onChange={(event) => setField("location", event.target.value)}
								disabled={saving}
								placeholder="Optional"
							/>
						</div>
						<div className="monitoring__writing-persona-field">
							<Label for="writing-persona-language">Language</Label>
							<Input
								id="writing-persona-language"
								value={form.language}
								onChange={(event) => setField("language", event.target.value)}
								disabled={saving}
							/>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-tone">Tone</Label>
							<Input
								id="writing-persona-tone"
								value={form.tone}
								onChange={(event) => setField("tone", event.target.value)}
								disabled={saving}
								placeholder="e.g. Professional"
							/>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-likes">Likes</Label>
							<Input
								id="writing-persona-likes"
								type="textarea"
								rows={2}
								value={form.likes}
								onChange={(event) => setField("likes", event.target.value)}
								disabled={saving}
							/>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-dislikes">Dislikes</Label>
							<Input
								id="writing-persona-dislikes"
								type="textarea"
								rows={2}
								value={form.dislikes}
								onChange={(event) => setField("dislikes", event.target.value)}
								disabled={saving}
							/>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-interests">Interests</Label>
							<Input
								id="writing-persona-interests"
								type="textarea"
								rows={2}
								value={form.interests}
								onChange={(event) => setField("interests", event.target.value)}
								disabled={saving}
							/>
						</div>
						<div className="monitoring__writing-persona-field monitoring__writing-persona-field--full">
							<Label for="writing-persona-notes">Notes</Label>
							<Input
								id="writing-persona-notes"
								type="textarea"
								rows={4}
								value={form.notes}
								onChange={(event) => setField("notes", event.target.value)}
								disabled={saving}
								placeholder="Optional context for the AI writer"
							/>
						</div>
					</div>
					{error ? <p className="text-danger small mb-0 mt-3">{error}</p> : null}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" outline onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button
						color="primary"
						type="submit"
						disabled={saving || !form.name.trim() || !form.profession.trim()}
					>
						{saving ? <Spinner size="sm" className="me-2" /> : null}
						Save
					</Button>
				</ModalFooter>
			</form>
		</Modal>
	);
}

export default function WritingAssistantPersonasPanel({ websiteId }) {
	const [personas, setPersonas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [listError, setListError] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState("create");
	const [editingPersona, setEditingPersona] = useState(null);
	const [saving, setSaving] = useState(false);
	const [modalError, setModalError] = useState("");
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleteError, setDeleteError] = useState("");
	const [deleting, setDeleting] = useState(false);

	const loadPersonas = useCallback(async () => {
		setLoading(true);
		setListError("");
		const res = await listMonitoringWritingAssistantPersonas(websiteId);
		setLoading(false);
		if (!res.ok) {
			setPersonas([]);
			setListError(res.message || "Could not load personas.");
			return;
		}
		setPersonas(res.personas);
	}, [websiteId]);

	useEffect(() => {
		loadPersonas();
	}, [loadPersonas]);

	function openCreateModal() {
		setModalMode("create");
		setEditingPersona(null);
		setModalError("");
		setModalOpen(true);
	}

	function openEditModal(persona) {
		setModalMode("edit");
		setEditingPersona(persona);
		setModalError("");
		setModalOpen(true);
	}

	function closeModal() {
		if (saving) return;
		setModalOpen(false);
		setEditingPersona(null);
		setModalError("");
	}

	async function handleSave(form) {
		const name = form.name.trim();
		const profession = form.profession.trim();
		if (!name || !profession) return;
		setSaving(true);
		setModalError("");
		const payload = { ...form, name };
		const res =
			modalMode === "edit" && editingPersona?.id != null
				? await updateMonitoringWritingAssistantPersona(
						websiteId,
						editingPersona.id,
						payload
					)
				: await createMonitoringWritingAssistantPersona(websiteId, payload);
		setSaving(false);
		if (!res.ok) {
			setModalError(res.message || "Could not save persona.");
			return;
		}
		setModalOpen(false);
		setEditingPersona(null);
		await loadPersonas();
	}

	function openDeleteModal(persona) {
		setDeleteError("");
		setDeleteTarget(persona);
	}

	function closeDeleteModal() {
		if (deleting) return;
		setDeleteTarget(null);
		setDeleteError("");
	}

	async function confirmDelete() {
		const id = deleteTarget?.id;
		if (id == null) return;
		setDeleting(true);
		setDeleteError("");
		const res = await deleteMonitoringWritingAssistantPersona(websiteId, id);
		setDeleting(false);
		if (!res.ok) {
			setDeleteError(res.message || "Could not delete persona.");
			return;
		}
		setDeleteTarget(null);
		await loadPersonas();
	}

	return (
		<div className="monitoring__writing-setup monitoring__writing-setup--personas">
			<div className="monitoring__writing-setup-head">
				<div className="monitoring__writing-setup-head-copy">
					<p className="monitoring__writing-setup-lead mb-0">
						Personas the AI Writing Assistant can write as for this website.
					</p>
					<p className="monitoring__writing-setup-sublead mb-0">
						Choose a voice when starting a draft from Content Strategy recommendations.
					</p>
				</div>
				<Button color="primary" size="sm" className="monitoring__writing-setup-action" onClick={openCreateModal}>
					<Plus size={14} aria-hidden className="me-1" />
					New persona
				</Button>
			</div>

			{loading ? (
				<div className="monitoring__writing-setup-card monitoring__writing-setup-card--status">
					<p className="monitoring__writing-setup-empty mb-0">Loading personas…</p>
				</div>
			) : listError ? (
				<div className="monitoring__writing-setup-card monitoring__writing-setup-card--status">
					<p className="text-danger small mb-0">{listError}</p>
				</div>
			) : personas.length === 0 ? (
				<div className="monitoring__writing-setup-card monitoring__writing-setup-card--empty">
					<div className="monitoring__writing-persona-empty">
						<span className="monitoring__writing-persona-empty-icon" aria-hidden>
							+
						</span>
						<h3 className="monitoring__writing-persona-empty-title">No personas yet</h3>
						<p className="monitoring__writing-persona-empty-text mb-0">
							Create a persona to guide tone, audience, and style when generating content.
						</p>
						<Button color="primary" size="sm" className="mt-3" onClick={openCreateModal}>
							<Plus size={14} aria-hidden className="me-1" />
							Create first persona
						</Button>
					</div>
				</div>
			) : (
				<div className="monitoring__writing-persona-grid">
					{personas.map((persona, index) => (
						<PersonaCard
							key={persona.id ?? `persona-${index}`}
							persona={persona}
							onEdit={openEditModal}
							onDelete={openDeleteModal}
						/>
					))}
				</div>
			)}

			<PersonaModal
				isOpen={modalOpen}
				mode={modalMode}
				initialForm={editingPersona ? personaToForm(editingPersona) : EMPTY_FORM}
				saving={saving}
				error={modalError}
				onClose={closeModal}
				onSave={handleSave}
			/>

			<PersonaDeleteModal
				persona={deleteTarget}
				deleting={deleting}
				error={deleteError}
				onClose={closeDeleteModal}
				onConfirm={confirmDelete}
			/>
		</div>
	);
}
